import React, { useEffect, useState } from 'react';
import { Image as KonvaImage, Layer, Rect, Stage, Text } from 'react-konva';
import useKonvaImage from '../../hooks/useKonvaImage';

const COLORS = ['#E74C3C', '#3498DB', '#2ECC71', '#F39C12', '#9B59B6'];
const clip = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function BoundingBoxCanvas({ imageUrl, labelClasses, boxes, onBoxesChange }) {
  const { containerRef, image, display } = useKonvaImage(imageUrl);

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawEnd, setDrawEnd] = useState(null);
  const [pendingBox, setPendingBox] = useState(null);
  const [pendingLabel, setPendingLabel] = useState('');
  const [dropdownPos, setDropdownPos] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setDrawStart(null);
        setDrawEnd(null);
        setPendingBox(null);
        setDropdownPos(null);
        setPendingLabel('');
        setSelectedIdx(null);
      } else if (e.key === 'Enter' && pendingBox && pendingLabel) {
        confirmPending(pendingBox, pendingLabel);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIdx !== null && !pendingBox) {
        onBoxesChange(boxes.filter((_, i) => i !== selectedIdx));
        setSelectedIdx(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingBox, pendingLabel, selectedIdx, boxes, onBoxesChange]);

  const confirmPending = (box, label) => {
    onBoxesChange([...boxes, { ...box, label }]);
    setPendingBox(null);
    setDropdownPos(null);
    setPendingLabel('');
  };

  const toPercent = (px, py) => ({
    x: clip(px / display.w, 0, 1),
    y: clip(py / display.h, 0, 1),
  });

  const handleMouseDown = (e) => {
    if (pendingBox) return;
    const pos = e.target.getStage().getPointerPosition();
    const pct = toPercent(pos.x, pos.y);
    setIsDrawing(true);
    setDrawStart(pct);
    setDrawEnd(pct);
    setSelectedIdx(null);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const pos = e.target.getStage().getPointerPosition();
    setDrawEnd(toPercent(pos.x, pos.y));
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const pos = e.target.getStage().getPointerPosition();
    const end = toPercent(pos.x, pos.y);

    const x = Math.min(drawStart.x, end.x);
    const y = Math.min(drawStart.y, end.y);
    const w = Math.abs(end.x - drawStart.x);
    const h = Math.abs(end.y - drawStart.y);
    setDrawStart(null);
    setDrawEnd(null);

    if (w < 0.01 || h < 0.01) return;

    const box = { x, y, w, h };
    const firstLabel = labelClasses && labelClasses.length > 0 ? labelClasses[0] : '';
    setPendingBox(box);
    setPendingLabel(firstLabel);

    const dpLeft = clip(pos.x + 8, 0, display.w - 190);
    const dpTop = clip(pos.y + 8, 0, display.h - 100);
    setDropdownPos({ left: dpLeft, top: dpTop });
  };

  const previewRect = isDrawing && drawStart && drawEnd ? {
    x: Math.min(drawStart.x, drawEnd.x) * display.w,
    y: Math.min(drawStart.y, drawEnd.y) * display.h,
    w: Math.abs(drawEnd.x - drawStart.x) * display.w,
    h: Math.abs(drawEnd.y - drawStart.y) * display.h,
  } : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', userSelect: 'none', lineHeight: 0 }}>
      {display.w > 0 && (
        <Stage
          width={display.w}
          height={display.h}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{ cursor: isDrawing ? 'crosshair' : 'default', display: 'block' }}
        >
          <Layer>
            {image && (
              <KonvaImage image={image} x={0} y={0} width={display.w} height={display.h} />
            )}

            {boxes.map((box, i) => {
              const color = COLORS[i % COLORS.length];
              const bx = box.x * display.w;
              const by = box.y * display.h;
              const bw = box.w * display.w;
              const bh = box.h * display.h;
              return (
                <React.Fragment key={i}>
                  <Rect
                    x={bx} y={by} width={bw} height={bh}
                    stroke={color} strokeWidth={selectedIdx === i ? 3 : 2}
                    fill={color + '33'}
                    onClick={() => setSelectedIdx(i === selectedIdx ? null : i)}
                  />
                  <Text
                    x={bx + 4} y={by + 4} text={box.label}
                    fontSize={13} fontStyle="bold" fill="#fff"
                    shadowColor={color} shadowBlur={4} listening={false}
                  />
                </React.Fragment>
              );
            })}

            {pendingBox && (
              <Rect
                x={pendingBox.x * display.w} y={pendingBox.y * display.h}
                width={pendingBox.w * display.w} height={pendingBox.h * display.h}
                stroke="#FFD700" strokeWidth={2} dash={[8, 4]}
                fill="rgba(255,215,0,0.12)" listening={false}
              />
            )}

            {previewRect && (
              <Rect
                x={previewRect.x} y={previewRect.y}
                width={previewRect.w} height={previewRect.h}
                stroke="#ffffff" strokeWidth={1.5} dash={[6, 3]}
                fill="rgba(255,255,255,0.08)" listening={false}
              />
            )}
          </Layer>
        </Stage>
      )}

      {pendingBox && dropdownPos && (
        <div style={{ ...styles.dropdown, left: dropdownPos.left, top: dropdownPos.top }}>
          <div style={styles.dropdownLabel}>Assign label</div>
          <select
            autoFocus
            value={pendingLabel}
            onChange={e => setPendingLabel(e.target.value)}
            style={styles.select}
          >
            {(labelClasses || []).map(lc => (
              <option key={lc} value={lc}>{lc}</option>
            ))}
          </select>
          <div style={styles.dropdownBtns}>
            <button
              style={{ ...styles.ddBtn, background: '#2ECC71' }}
              onClick={() => confirmPending(pendingBox, pendingLabel)}
            >
              OK
            </button>
            <button
              style={{ ...styles.ddBtn, background: '#E74C3C' }}
              onClick={() => { setPendingBox(null); setDropdownPos(null); setPendingLabel(''); }}
            >
              Cancel
            </button>
          </div>
          <div style={styles.dropdownHint}>Enter to confirm · Esc to cancel</div>
        </div>
      )}
    </div>
  );
}

const styles = {
  dropdown: {
    position: 'absolute', background: '#1e293b', border: '1px solid #334155',
    borderRadius: '8px', padding: '10px', zIndex: 200,
    boxShadow: '0 4px 16px rgba(0,0,0,0.5)', minWidth: '180px',
  },
  dropdownLabel: { color: '#94a3b8', fontSize: '0.75rem', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase' },
  select: {
    width: '100%', padding: '5px 8px', borderRadius: '4px',
    border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0',
    fontSize: '0.9rem', marginBottom: '8px',
  },
  dropdownBtns: { display: 'flex', gap: '6px' },
  ddBtn: { flex: 1, padding: '5px', border: 'none', borderRadius: '4px', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  dropdownHint: { color: '#64748b', fontSize: '0.7rem', marginTop: '6px', textAlign: 'center' },
};
